import {makeAutoObservable} from 'mobx';
import {toast} from 'react-toastify';
import PostsRepository from '../../../data/repositories/PostsRepository';
import CoursesRepository from '../../../data/repositories/CoursesRepository';
import LessonContentRepository from '../../../data/repositories/LessonContentRepository';
import LessonsRepository from '../../../data/repositories/LessonsRepository';
import {Course} from '../../../data/models/Course/Course';
import {LessonContentBase} from '../../../data/models/LessonContent/LessonContentBase';
import {CreatePostPayload, PostListItem} from '../../../data/models/Posts/Post';
import {ProjectFilter} from '../../../data/models/Projects/Project';
import LoadingModule from '../../../modules/LoadingModule/LoadingModule';
import {getErrorFromCode, serverErrorCodes} from '../../../data/errorCodes';
import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import {postsText} from '../postsText';

export const POSTS_PER_PAGE = 12;

class PostsStore {
  private postsRepo = new PostsRepository();
  private coursesRepo = new CoursesRepository();
  private lessonContentRepo = new LessonContentRepository();
  private lessonsRepo = new LessonsRepository();

  private loadingModule = new LoadingModule();
  private editLoadingModule = new LoadingModule();

  constructor() {
    makeAutoObservable(this);
  }

  public get isLoading() {
    return this.loadingModule.isLoading;
  }

  public get isEditLoading() {
    return this.editLoadingModule.isLoading;
  }

  private handleErrors(errors?: string[]) {
    if (errors && errors[0]) {
      toast.error(getErrorFromCode(errors[0]), toastConfig);
    }
  }

  public async getPosts(
    organizationId: string,
    page: number,
    search?: string,
    filter?: ProjectFilter,
  ) {
    this.loadingModule.startLoading();
    const result = await this.postsRepo.getPosts(
      organizationId,
      page,
      POSTS_PER_PAGE,
      search,
      filter,
    );
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    this.handleErrors(result.errors);
    return {
      pagesCount: 0,
      data: [] as PostListItem[],
    };
  }

  // There is no get-by-id endpoint, so a direct link walks the list pages.
  public async getPostById(organizationId: string, postId: string) {
    let page = 0;
    let pagesCount = 1;
    while (page < pagesCount) {
      const result = await this.getPosts(organizationId, page);
      const found = result.data.find(item => item.id === postId);
      if (found) return found;
      pagesCount = result.pagesCount;
      page++;
    }
    return undefined;
  }

  public async createPost(organizationId: string, payload: CreatePostPayload) {
    this.editLoadingModule.startLoading();
    const result = await this.postsRepo.createPost(organizationId, payload);
    this.editLoadingModule.endLoading();
    if (!result.success) {
      this.handleErrors(result.errors);
      return undefined;
    }
    toast.success(postsText.postCreated, toastConfig);
    return result.data?.courseId;
  }

  public async getPostCourse(organizationId: string, postId: string) {
    this.editLoadingModule.startLoading();
    const result = await this.coursesRepo.getCourse(organizationId, postId);
    this.editLoadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    this.handleErrors(result.errors);
    return undefined;
  }

  public async editPost(
    organizationId: string,
    course: Course,
    lessonId?: string,
    cover?: File,
  ) {
    this.editLoadingModule.startLoading();
    const result = await this.coursesRepo.editCourse(organizationId, course);
    if (!result.success) {
      this.editLoadingModule.endLoading();
      this.handleErrors(result.errors);
      return false;
    }
    // Learners read the caption from the lesson, not the course.
    if (lessonId) {
      const lesson = await this.lessonsRepo.getLessonById(
        organizationId,
        lessonId,
      );
      const edited =
        lesson.success && lesson.data
          ? await this.lessonsRepo.editLesson(organizationId, {
              ...lesson.data,
              title: course.title,
              description: course.description,
            })
          : lesson;
      if (!edited.success) {
        this.editLoadingModule.endLoading();
        this.handleErrors(edited.errors);
        return false;
      }
    }
    if (cover && lessonId) {
      const poster = await this.lessonsRepo.uploadPoster(
        organizationId,
        lessonId,
        cover,
      );
      if (!poster.success) {
        this.editLoadingModule.endLoading();
        this.handleErrors(poster.errors);
        return false;
      }
      const image = await this.coursesRepo.editCoursePoster(
        organizationId,
        course.id!,
        cover,
      );
      if (!image.success) {
        this.editLoadingModule.endLoading();
        this.handleErrors(image.errors);
        return false;
      }
    }
    // Learners keep playing the published copy until it is republished.
    const republished = await this.republishPost(organizationId, course.id!);
    this.editLoadingModule.endLoading();
    if (republished) {
      toast.success(postsText.postUpdated, toastConfig);
    }
    return true;
  }

  public async reorderPostMedia(
    organizationId: string,
    postId: string,
    lessonId: string,
    orderedInternalIds: string[],
  ) {
    this.editLoadingModule.startLoading();
    const result = await this.lessonContentRepo.reorderLessonContent(
      organizationId,
      lessonId,
      orderedInternalIds,
    );
    if (!result.success) {
      this.editLoadingModule.endLoading();
      this.handleErrors(result.errors);
      return false;
    }
    const republished = await this.republishPost(organizationId, postId);
    this.editLoadingModule.endLoading();
    if (republished) {
      toast.success(postsText.orderUpdated, toastConfig);
    }
    return true;
  }

  public async renamePostMedia(
    organizationId: string,
    postId: string,
    lessonId: string,
    node: LessonContentBase,
  ) {
    this.editLoadingModule.startLoading();
    const result = await this.lessonContentRepo.editLessonContent(
      organizationId,
      lessonId,
      node,
    );
    if (!result.success) {
      this.editLoadingModule.endLoading();
      this.handleErrors(result.errors);
      return false;
    }
    const republished = await this.republishPost(organizationId, postId);
    this.editLoadingModule.endLoading();
    if (republished) {
      toast.success(postsText.mediaRenamed, toastConfig);
    }
    return true;
  }

  public async removePostMedia(
    organizationId: string,
    postId: string,
    lessonId: string,
    orderedInternalIds: string[],
    index: number,
  ) {
    this.editLoadingModule.startLoading();
    const internalId = orderedInternalIds[index];
    // Removing the first item takes the rest of the run with it, so it is
    // moved to the end before it goes.
    if (index === 0) {
      const rotated = [...orderedInternalIds.slice(1), internalId];
      const reordered = await this.lessonContentRepo.reorderLessonContent(
        organizationId,
        lessonId,
        rotated,
      );
      if (!reordered.success) {
        this.editLoadingModule.endLoading();
        this.handleErrors(reordered.errors);
        return false;
      }
    }
    const result = await this.lessonContentRepo.softDeleteLessonContent(
      organizationId,
      lessonId,
      internalId,
    );
    if (!result.success) {
      this.editLoadingModule.endLoading();
      this.handleErrors(result.errors);
      return false;
    }
    const republished = await this.republishPost(organizationId, postId);
    this.editLoadingModule.endLoading();
    if (republished) {
      toast.success(postsText.mediaRemoved, toastConfig);
    }
    return true;
  }

  public async deletePost(organizationId: string, postId: string) {
    this.editLoadingModule.startLoading();
    const result = await this.coursesRepo.deleteCourse(organizationId, postId);
    this.editLoadingModule.endLoading();
    if (!result.success) {
      this.handleErrors(result.errors);
      return false;
    }
    toast.success(postsText.postDeleted, toastConfig);
    return true;
  }

  public async getPostContent(organizationId: string, lessonId: string) {
    this.loadingModule.startLoading();
    const result = await this.lessonContentRepo.getLessonContent(
      organizationId,
      lessonId,
    );
    this.loadingModule.endLoading();
    if (result.success && result.data) {
      return result.data;
    }
    this.handleErrors(result.errors);
    return undefined;
  }

  // A post is published on creation, so this only ever syncs an edit. An
  // unpublished one answers entity_not_found and there is nothing to do.
  private async republishPost(organizationId: string, postId: string) {
    const result = await this.coursesRepo.republishCourse(
      organizationId,
      postId,
    );
    if (result.success) return true;
    if (
      result.errors &&
      result.errors[0] === serverErrorCodes.ENTITY_NOT_FOUND
    ) {
      return true;
    }
    this.handleErrors(result.errors);
    return false;
  }
}

export default PostsStore;
